import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';
import { toast } from 'sonner';
import { CheckCircle2, AlertCircle, Mail } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

type Outcome = 'success' | 'declined' | 'error';

function readOutcome(value: string | null): Outcome {
  return value === 'success' || value === 'declined' ? value : 'error';
}

/**
 * Where the browser lands after the Gmail connection finishes (FIN-1235). Google no longer
 * redirects here: it redirects to the server-side `gmail-oauth-callback` function, which
 * completes the connection and then sends the browser to THIS page on whichever consultant app
 * started the flow, with only an `outcome` flag. So this page holds no OAuth code or state, calls
 * no API, and is safe to reload.
 */
export function GmailCallbackHandler() {
  const t = useTranslations('consultantMessages.gmailCallback');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const outcome = readOutcome(searchParams.get('outcome'));

  useEffect(() => {
    if (outcome === 'success') {
      toast.success(t('connected'));
      navigate('/messages');
    }
  }, [outcome, navigate, t]);

  return (
    <div className="flex items-center justify-center min-h-screen bg-background">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            {t('title')}
          </CardTitle>
          <CardDescription>
            {outcome === 'success' ? t('success') : t('failed')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {outcome === 'success' ? (
            <div className="flex flex-col items-center justify-center py-8">
              <CheckCircle2 className="h-12 w-12 text-green-500 mb-4" />
              <p className="text-sm text-muted-foreground text-center">{t('redirecting')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-col items-center justify-center py-8">
                <AlertCircle className="h-12 w-12 text-destructive mb-4" />
                <p className="text-sm text-center text-destructive">{t(outcome === 'declined' ? 'declined' : 'generic')}</p>
              </div>
              <Button onClick={() => navigate('/messages')} className="w-full">
                {t('returnToMessages')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default GmailCallbackHandler;
